import {
  type ItemDto,
  type ItemFinish,
  type ItemMaterial,
  type ItemOrientation,
  type ItemPriceRequestDto,
  type ItemPriceResultDto,
  MarketPricingConfigEnum,
  type ProductPublicDto,
} from "@sticker-giant/sg-frontend-common";
import { getMaterialBounds } from "./material";
import { getStartingQuantity } from "./quantities";

/** Map of "productSku:materialSku" → total starting price */
export type MaterialStartingPriceMap = Map<string, number>;

interface PricingOverrides {
  material?: ItemMaterial;
  finish?: ItemFinish;
  height?: string;
  width?: string;
  rollCount?: number | null;
  rollQty?: number | null;
  orientation?: ItemOrientation;
}

function getDefaultDimension(value: number | undefined | null, fallback: number | undefined | null): string {
  return (value ?? fallback ?? 0).toString();
}
interface BuildBaseItemParams {
  product: ProductPublicDto;
  customerSkus?: string[];
  overrides?: PricingOverrides;
}

function buildBaseItem({ product, overrides, customerSkus }: BuildBaseItemParams): ItemDto {
  const material = overrides?.material ?? product.defaultMaterial;
  const dimensions = getMaterialBounds(product, material);
  const minHeight = dimensions?.minHeight ?? product.minCustomHeight;
  const minWidth = dimensions?.minWidth ?? product.minCustomWidth;

  return {
    addons: product.requiredAddons.map(addon => addon.sku),
    finish: overrides?.finish ?? product.defaultFinishing ?? product.finishingOptions[0]?.sku,
    height: overrides?.height ?? getDefaultDimension(product.defaultHeight, minHeight),
    width: overrides?.width ?? getDefaultDimension(product.defaultWidth, minWidth),
    otherSkus: customerSkus ?? [],
    rollCount: overrides?.rollCount ?? null,
    rollQty: overrides?.rollQty ?? null,
    orientation: overrides?.orientation,
    marketingName: product.sku,
    material,
    product: product.pricingProductType.sku,
  };
}

function buildRequest(item: ItemDto, quantities: number[], product: ProductPublicDto): ItemPriceRequestDto {
  return {
    item,
    quantities,
    config: {
      roundPerUnitPricing: true,
      useMarketPricing: product.marketPricingConfig === MarketPricingConfigEnum.ALWAYS_ACTIVE,
    },
  };
}

export function buildStartingPriceRequest(
  product: ProductPublicDto,
  quantity?: number,
  material?: ItemMaterial,
): ItemPriceRequestDto {
  const overrides = material ? { material } : undefined;
  const startingQuantity = getStartingQuantity(product, quantity, material);
  const item = buildBaseItem({ product, overrides });
  return buildRequest({ ...item, quantity: startingQuantity }, [startingQuantity], product);
}
interface AllQuantitiesPricingRequestParams extends BuildBaseItemParams {
  quantities: number[];
}

interface CustomQuantityPricingRequestParams extends BuildBaseItemParams {
  customQuantity: number;
}

export function buildAllQuantitiesPricingRequest({
  product,
  quantities,
  overrides,
  customerSkus,
}: AllQuantitiesPricingRequestParams): ItemPriceRequestDto {
  return buildRequest(buildBaseItem({ product, overrides, customerSkus }), quantities, product);
}

export function buildCustomQuantityPricingRequest({
  product,
  customQuantity,
  overrides,
  customerSkus,
}: CustomQuantityPricingRequestParams): ItemPriceRequestDto {
  const item = buildBaseItem({ product, overrides, customerSkus });
  return buildRequest({ ...item, quantity: customQuantity }, [customQuantity], product);
}

export function getItemPrice(
  quantity: number,
  pricingData?: ItemPriceResultDto,
  customPricingData?: ItemPriceResultDto,
) {
  const presetPrice = pricingData?.quantityPrices.find(qp => qp.quantity === quantity)?.price;
  const customPrice = customPricingData?.quantityPrices[0]?.price;
  const totalPrice = Number(presetPrice ?? customPrice);
  return Number.isNaN(totalPrice) ? undefined : totalPrice;
}
